import {
	Binoculars,
	CalendarBlank,
	ChartLineUp,
	ChatCircle,
	NotePencil,
	ShareNetwork,
} from "@phosphor-icons/react";
import { Link, useLocation } from "@tanstack/react-router";
import {
	ImageIcon,
	LifeBuoy,
	PanelLeftClose,
	PanelLeftOpen,
	Plus,
	Sparkles,
} from "lucide-react";
import { motion } from "motion/react";
import { useCallback, useContext, useEffect, useState } from "react";
import { LogoIcon } from "@/components/brand/logo-icon";
import { DashboardContext } from "@/components/dashboard/DashboardContext";
import { signOutAndNavigate } from "@/components/header/shared";
import { UserMenu } from "@/components/header/UserMenu";
import { WorkspaceSwitcher } from "@/components/header/WorkspaceSwitcher";
import { useProDialog } from "@/components/ProDialog";
import { Kbd } from "@/components/ui/kbd";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { useSession } from "@/hooks/auth-hooks";
import { useSubscription } from "@/hooks/use-subscription";
import type { SubscriptionTier } from "@/lib/plans";
import { cn } from "@/lib/utils";

function getMembershipLabel(tier: SubscriptionTier): string | null {
	if (tier === "max") return "Max Member";
	if (tier === "pro") return "Pro Member";
	return null;
}

const COLLAPSED_STORAGE_KEY = "sidebar-collapsed";

type IconProps = { className?: string };

type NavItem = {
	to: string;
	label: string;
	icon: (props: IconProps) => React.ReactNode;
	requiresTier?: "max";
};

type NavSection = {
	label: string;
	items: NavItem[];
};

const NAV_SECTIONS: NavSection[] = [
	{
		label: "Overview",
		items: [
			{
				to: "/dashboard",
				label: "Chat",
				icon: (p) => <ChatCircle {...p} weight="duotone" />,
			},
			{
				to: "/dashboard/connect",
				label: "Connect",
				icon: (p) => <ShareNetwork {...p} weight="duotone" />,
			},
		],
	},
	{
		label: "Publish",
		items: [
			{
				to: "/dashboard/posts",
				label: "Posts",
				icon: (p) => <NotePencil {...p} weight="duotone" />,
			},
			{
				to: "/dashboard/calendar",
				label: "Calendar",
				icon: (p) => <CalendarBlank {...p} weight="duotone" />,
			},
			{
				to: "/dashboard/media",
				label: "Media",
				icon: (p) => <ImageIcon {...p} />,
			},
		],
	},
	{
		label: "Monitor",
		items: [
			{
				to: "/dashboard/analytics",
				label: "Analytics",
				icon: (p) => <ChartLineUp {...p} weight="duotone" />,
			},
			{
				to: "/dashboard/listening",
				label: "Listening",
				icon: (p) => <Binoculars {...p} weight="duotone" />,
			},
		],
	},
];

const MOBILE_NAV_ITEMS: NavItem[] = [
	{
		to: "/dashboard/posts",
		label: "Posts",
		icon: (p) => <NotePencil {...p} weight="duotone" />,
	},
	{
		to: "/dashboard/calendar",
		label: "Calendar",
		icon: (p) => <CalendarBlank {...p} weight="duotone" />,
	},
	{
		to: "/dashboard/connect",
		label: "Connect",
		icon: (p) => <ShareNetwork {...p} weight="duotone" />,
	},
	{
		to: "/dashboard/media",
		label: "Media",
		icon: (p) => <ImageIcon {...p} />,
	},
];

function isItemActive(itemTo: string, pathname: string) {
	const clean = pathname.split(/[?#]/)[0];
	if (itemTo === "/dashboard") {
		return (
			clean === "/dashboard" ||
			clean === "/dashboard/" ||
			clean === "/dashboard/chat"
		);
	}
	return clean === itemTo || clean.startsWith(`${itemTo}/`);
}

function useCollapsedState() {
	const [collapsed, setCollapsed] = useState<boolean>(() => {
		if (typeof window === "undefined") return false;
		return window.localStorage.getItem(COLLAPSED_STORAGE_KEY) === "true";
	});

	useEffect(() => {
		if (typeof window === "undefined") return;
		window.localStorage.setItem(COLLAPSED_STORAGE_KEY, String(collapsed));
	}, [collapsed]);

	const toggle = useCallback(() => setCollapsed((v) => !v), []);

	useEffect(() => {
		if (typeof window === "undefined") return;
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key !== "b" && e.key !== "B") return;
			if (e.metaKey || e.ctrlKey || e.altKey) return;
			const target = e.target as HTMLElement | null;
			if (!target) return;
			if (target.isContentEditable) return;
			const tag = target.tagName;
			if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
			e.preventDefault();
			toggle();
		};
		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [toggle]);

	return { collapsed, toggle };
}

function SidebarSectionLabel({
	children,
	className,
}: {
	children: React.ReactNode;
	className?: string;
}) {
	return (
		<p
			className={cn(
				"mb-2 px-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground",
				className,
			)}
		>
			{children}
		</p>
	);
}

export function DashboardSidebar() {
	const location = useLocation();
	const { session, user } = useSession();
	const dashboardCtx = useContext(DashboardContext);
	const { openProDialog } = useProDialog();
	const {
		isActive: hasActiveSubscription,
		isLoading: isSubscriptionLoading,
		tier,
	} = useSubscription(user?.id);
	const [isSigningOut, setIsSigningOut] = useState(false);
	const { collapsed, toggle: toggleCollapsed } = useCollapsedState();

	const isImpersonating = !!session?.impersonatedBy;
	const showUpgradeButton = !isSubscriptionLoading && !hasActiveSubscription;
	const hasMaxTier = tier === "max";
	const membershipLabel = getMembershipLabel(tier);

	const handleSignOut = async () => {
		if (isSigningOut) return;
		setIsSigningOut(true);
		try {
			await signOutAndNavigate(location.pathname);
		} catch (error) {
			console.error("Sign out failed:", error);
			setIsSigningOut(false);
		}
	};

	const widthClass = collapsed ? "w-16" : "w-16 sm:w-64";
	const paddingClass = collapsed ? "px-2 pb-2" : "px-2 pb-2 sm:px-4 sm:pb-4";
	const rowLabelClass = collapsed
		? "hidden"
		: "hidden min-w-0 flex-1 truncate text-left sm:block";
	const rowBadgeClass = collapsed
		? "hidden"
		: "ml-auto hidden shrink-0 text-[11px] font-medium tabular-nums text-muted-foreground sm:inline-flex";
	const justifyClass = collapsed
		? "justify-center"
		: "justify-center sm:justify-start";
	const iconButtonClass =
		"rounded-md text-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring/40";
	const navItemClass = `group relative flex min-h-10 w-full cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-sm font-medium leading-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring/40 ${justifyClass}`;
	const quietItemClass =
		"text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground";
	const activeItemClass = "text-sidebar-accent-foreground";
	const activeIndicatorTransition = {
		type: "spring" as const,
		stiffness: 380,
		damping: 32,
		mass: 0.6,
	};

	return (
		<aside
			aria-label="Dashboard navigation"
			className={`hidden h-full shrink-0 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground transition-[width] duration-200 sm:flex ${widthClass} ${paddingClass}`}
		>
			<div
				className={`flex min-h-[56px] items-center ${
					collapsed ? "justify-center" : "justify-center sm:justify-between"
				}`}
			>
				<Link
					to="/dashboard"
					aria-label="AgentReacher home"
					className={`flex min-w-0 items-center rounded-md transition-colors hover:bg-sidebar-accent ${
						collapsed ? "size-10 justify-center" : "gap-2 p-1 sm:flex-1"
					}`}
				>
					<LogoIcon className="size-8 shrink-0 rounded-lg" />
					<span
						className={`${collapsed ? "hidden" : "hidden sm:flex"} min-w-0 flex-col gap-0.5 leading-none`}
					>
						<span className="truncate text-sm font-medium">AgentReacher</span>
					</span>
				</Link>
			</div>

			{dashboardCtx && (
				<div
					className={cn(
						"-mx-2 mb-4 px-2 sm:-mx-4 sm:px-4",
						!collapsed && "border-y border-border py-2",
						collapsed && "flex justify-center",
					)}
				>
					<WorkspaceSwitcher collapsed={collapsed} />
				</div>
			)}

			{!collapsed && <SidebarSectionLabel>Action</SidebarSectionLabel>}
			{collapsed ? (
				<Tooltip>
					<TooltipTrigger asChild>
						{hasActiveSubscription ? (
							<Link
								to="/dashboard/compose"
								aria-label="Create Post"
								className="mb-4 flex size-9 items-center justify-center self-center rounded-md bg-sidebar-primary text-sidebar-primary-foreground transition-colors hover:bg-sidebar-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring/40"
							>
								<Plus className="size-4" />
							</Link>
						) : (
							<button
								type="button"
								onClick={() => openProDialog()}
								aria-label="Create Post"
								className="mb-4 flex size-9 items-center justify-center self-center rounded-md bg-sidebar-primary text-sidebar-primary-foreground transition-colors hover:bg-sidebar-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring/40"
							>
								<Plus className="size-4" />
							</button>
						)}
					</TooltipTrigger>
					<TooltipContent side="right">Create Post</TooltipContent>
				</Tooltip>
			) : hasActiveSubscription ? (
				<Link
					to="/dashboard/compose"
					aria-label="Create Post"
					className={`mb-5 flex min-h-10 w-full items-center gap-3 rounded-md bg-sidebar-primary px-2 py-2 text-sm font-medium leading-none text-sidebar-primary-foreground transition-colors hover:bg-sidebar-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring/40 ${justifyClass}`}
				>
					<Plus className="size-4 shrink-0" />
					<span className={rowLabelClass}>Create</span>
				</Link>
			) : (
				<button
					type="button"
					onClick={() => openProDialog()}
					aria-label="Create Post"
					className={`mb-5 flex min-h-10 w-full items-center gap-3 rounded-md bg-sidebar-primary px-2 py-2 text-sm font-medium leading-none text-sidebar-primary-foreground transition-colors hover:bg-sidebar-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring/40 ${justifyClass}`}
				>
					<Plus className="size-4 shrink-0" />
					<span className={rowLabelClass}>Create</span>
				</button>
			)}

			<div
				className={collapsed ? "flex flex-col gap-1" : "flex flex-col gap-5"}
			>
				{NAV_SECTIONS.map((section) => (
					<nav
						key={section.label}
						aria-label={`${section.label} navigation`}
						className="min-w-0"
					>
						{!collapsed && (
							<SidebarSectionLabel>{section.label}</SidebarSectionLabel>
						)}
						<div className="flex flex-col gap-1">
							{section.items.map(({ to, label, icon: Icon, requiresTier }) => {
								const active = isItemActive(to, location.pathname);
								const isLocked = requiresTier === "max" && !hasMaxTier;
								const className = `${navItemClass} ${
									active ? activeItemClass : quietItemClass
								}`;
								const content = (
									<>
										{active && (
											<>
												<motion.span
													layoutId="sidebar-active-bg"
													className="absolute inset-0 z-0 rounded-md bg-sidebar-accent"
													transition={activeIndicatorTransition}
												/>
												<motion.span
													layoutId="sidebar-active-pill"
													className="absolute right-1 top-1/2 z-0 h-5 w-[3px] -translate-y-1/2 rounded-full bg-primary"
													transition={activeIndicatorTransition}
												/>
											</>
										)}
										<Icon className="relative z-10 size-4 shrink-0" />
										<span className={cn("relative z-10", rowLabelClass)}>
											{label}
										</span>
										{requiresTier === "max" && (
											<span className={cn("relative z-10", rowBadgeClass)}>
												Max
											</span>
										)}
									</>
								);
								const inner = isLocked ? (
									<button
										key={to}
										type="button"
										onClick={() => openProDialog({ tier: requiresTier })}
										className={className}
										aria-label={label}
									>
										{content}
									</button>
								) : (
									<Link
										key={to}
										to={to}
										aria-current={active ? "page" : undefined}
										aria-label={label}
										className={className}
									>
										{content}
									</Link>
								);

								if (collapsed) {
									return (
										<Tooltip key={to}>
											<TooltipTrigger asChild>{inner}</TooltipTrigger>
											<TooltipContent side="right">{label}</TooltipContent>
										</Tooltip>
									);
								}
								return inner;
							})}
						</div>
					</nav>
				))}
			</div>

			<div className="mt-auto flex flex-col gap-1 border-t border-sidebar-border pt-4">
				{!collapsed && <SidebarSectionLabel>Account</SidebarSectionLabel>}
				{collapsed ? (
					<Tooltip>
						<TooltipTrigger asChild>
							<Link
								to="/dashboard/support"
								aria-label="Support"
								className={`flex min-h-10 w-full items-center justify-center text-sm ${iconButtonClass}`}
							>
								<LifeBuoy className="size-4 shrink-0" />
							</Link>
						</TooltipTrigger>
						<TooltipContent side="right">Support</TooltipContent>
					</Tooltip>
				) : (
					<Link
						to="/dashboard/support"
						className={`flex min-h-10 w-full items-center gap-3 rounded-md px-2 py-2 text-sm font-medium leading-none ${quietItemClass} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring/40 ${justifyClass}`}
					>
						<LifeBuoy className="size-4 shrink-0" />
						<span className={rowLabelClass}>Support</span>
					</Link>
				)}

				{showUpgradeButton &&
					(collapsed ? (
						<Tooltip>
							<TooltipTrigger asChild>
								<button
									type="button"
									onClick={openProDialog}
									aria-label="Upgrade"
									className={`flex min-h-10 w-full items-center justify-center text-sm ${iconButtonClass}`}
								>
									<Sparkles className="size-4 shrink-0" />
								</button>
							</TooltipTrigger>
							<TooltipContent side="right">Upgrade</TooltipContent>
						</Tooltip>
					) : (
						<button
							type="button"
							onClick={openProDialog}
							className={`flex min-h-10 w-full items-center gap-3 rounded-md px-2 py-2 text-sm font-medium leading-none ${quietItemClass} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring/40 ${justifyClass}`}
						>
							<Sparkles className="size-4 shrink-0" />
							<span className={rowLabelClass}>Upgrade</span>
						</button>
					))}

				<Tooltip>
					<TooltipTrigger asChild>
						<button
							type="button"
							onClick={toggleCollapsed}
							aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
							className={cn(
								"hidden items-center text-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring/40 sm:flex",
								collapsed
									? "size-9 justify-center self-center rounded-md"
									: "min-h-9 w-full justify-start gap-3 rounded-md px-2 py-2 text-sm font-medium leading-none",
							)}
						>
							{collapsed ? (
								<PanelLeftOpen className="size-4" />
							) : (
								<PanelLeftClose className="size-4 shrink-0" />
							)}
							<span className={rowLabelClass}>Collapse</span>
						</button>
					</TooltipTrigger>
					<TooltipContent side="right" className="flex items-center gap-2">
						{collapsed ? "Expand sidebar" : "Collapse sidebar"}
						<Kbd>B</Kbd>
					</TooltipContent>
				</Tooltip>

				{user && (
					<div className="mt-2 flex border-t border-sidebar-border pt-2">
						<UserMenu
							user={user}
							isImpersonating={isImpersonating}
							showUpgradeButton={false}
							isSigningOut={isSigningOut}
							onUpgrade={openProDialog}
							onSignOut={() => {
								void handleSignOut();
							}}
							menuSide="top"
							menuAlign="end"
							menuSideOffset={8}
							wrapperClassName="w-full"
							triggerClassName={
								collapsed
									? "flex size-9 w-full items-center justify-center rounded-md hover:bg-sidebar-accent transition-colors group cursor-pointer"
									: "flex w-full items-center justify-start gap-2 rounded-md px-1.5 py-1 hover:bg-sidebar-accent transition-colors group cursor-pointer"
							}
							showUserName={!collapsed}
							showChevron={false}
							userNameClassName="min-w-0 flex-1 truncate text-left text-xs font-medium text-sidebar-foreground"
							membershipLabel={membershipLabel}
						/>
					</div>
				)}
			</div>
		</aside>
	);
}

export function DashboardMobileTopBar() {
	const location = useLocation();
	const { session, user } = useSession();
	const { openProDialog } = useProDialog();
	const {
		isActive: hasActiveSubscription,
		isLoading: isSubscriptionLoading,
		tier,
	} = useSubscription(user?.id);
	const [isSigningOut, setIsSigningOut] = useState(false);

	if (!user) return null;

	const isImpersonating = !!session?.impersonatedBy;
	const showUpgradeButton = !isSubscriptionLoading && !hasActiveSubscription;
	const membershipLabel = getMembershipLabel(tier);

	const handleSignOut = async () => {
		if (isSigningOut) return;
		setIsSigningOut(true);
		try {
			await signOutAndNavigate(location.pathname);
		} catch (error) {
			console.error("Sign out failed:", error);
			setIsSigningOut(false);
		}
	};

	return (
		<header className="sticky top-0 z-30 border-b border-sidebar-border bg-sidebar/95 px-4 pb-2 pt-[calc(env(safe-area-inset-top)+0.5rem)] text-sidebar-foreground backdrop-blur sm:hidden">
			<div className="flex min-h-11 items-center gap-2">
				<Link
					to="/dashboard"
					aria-label="AgentReacher home"
					className="flex size-10 shrink-0 items-center justify-center rounded-md transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
				>
					<LogoIcon className="size-7" />
				</Link>
				<div className="min-w-0 flex-1">
					<WorkspaceSwitcher variant="compact" />
				</div>
				<Link
					to="/dashboard/support"
					aria-label="Support"
					className="relative flex size-10 shrink-0 items-center justify-center rounded-md border border-border bg-background text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
				>
					<LifeBuoy className="size-4" />
				</Link>
				<UserMenu
					user={user}
					isImpersonating={isImpersonating}
					showUpgradeButton={showUpgradeButton}
					isSigningOut={isSigningOut}
					onUpgrade={openProDialog}
					onSignOut={() => {
						void handleSignOut();
					}}
					menuSide="bottom"
					menuAlign="end"
					menuSideOffset={8}
					wrapperClassName="shrink-0"
					triggerClassName="flex size-10 items-center justify-center rounded-md border border-border bg-background transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
					showUserName={false}
					showChevron={false}
					membershipLabel={membershipLabel}
				/>
			</div>
		</header>
	);
}

export function DashboardBottomNav() {
	const location = useLocation();
	const { user } = useSession();
	const { openProDialog } = useProDialog();
	const { isActive: hasActiveSubscription, tier } = useSubscription(user?.id);
	const hasMaxTier = tier === "max";

	const fabClass =
		"flex size-14 shrink-0 -translate-y-5 items-center justify-center rounded-full bg-blue-600 text-white shadow-xl transition-colors hover:bg-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

	const renderTab = ({ to, label, icon: Icon, requiresTier }: NavItem) => {
		const active = isItemActive(to, location.pathname);
		const isLocked = requiresTier === "max" && !hasMaxTier;
		const className = `flex min-w-[44px] flex-1 flex-col items-center justify-center gap-0.5 rounded-md px-1 py-1 text-[10px] font-semibold leading-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
			active
				? "text-blue-600 dark:text-blue-400"
				: "font-medium text-foreground/50 hover:text-foreground"
		}`;
		const content = (
			<>
				<Icon className="size-5 shrink-0" />
				<span className="max-w-full truncate">{label}</span>
			</>
		);
		return isLocked ? (
			<button
				key={to}
				type="button"
				onClick={() => openProDialog({ tier: requiresTier })}
				className={className}
				aria-label={label}
			>
				{content}
			</button>
		) : (
			<Link
				key={to}
				to={to}
				aria-current={active ? "page" : undefined}
				aria-label={label}
				className={className}
			>
				{content}
			</Link>
		);
	};

	const fab = hasActiveSubscription ? (
		<Link to="/dashboard/compose" aria-label="Create Post" className={fabClass}>
			<Plus className="size-6 shrink-0" />
		</Link>
	) : (
		<button
			type="button"
			onClick={() => openProDialog()}
			aria-label="Create Post"
			className={fabClass}
		>
			<Plus className="size-6 shrink-0" />
		</button>
	);

	const leading = MOBILE_NAV_ITEMS.slice(0, 2);
	const trailing = MOBILE_NAV_ITEMS.slice(2);
	const showChatPill = !isItemActive("/dashboard", location.pathname);

	return (
		<>
			{showChatPill ? (
				<Link
					to="/dashboard"
					aria-label="Open chat"
					className="fixed bottom-[calc(env(safe-area-inset-bottom)+5.25rem)] right-4 z-50 flex items-center gap-2 rounded-full border border-sidebar-border bg-card/90 px-3.5 py-2 text-sm font-medium text-foreground shadow-md backdrop-blur transition-colors hover:bg-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:hidden"
				>
					<ChatCircle className="size-4 shrink-0" weight="duotone" />
					Chat
				</Link>
			) : null}
			<nav
				aria-label="Dashboard navigation"
				className="fixed inset-x-0 bottom-0 z-40 border-t border-sidebar-border bg-sidebar/95 px-2 pb-[calc(env(safe-area-inset-bottom)+0.25rem)] pt-1.5 text-sidebar-foreground shadow-[0_-12px_32px_rgba(0,0,0,0.08)] backdrop-blur sm:hidden"
			>
				<div className="mx-auto flex max-w-md items-center gap-1">
					{leading.map(renderTab)}
					{fab}
					{trailing.map(renderTab)}
				</div>
			</nav>
		</>
	);
}
